<template>
  <div class="w-full max-w-sm px-4 sm:px-0">

    <!-- ── Modo: domínio principal — encontrar clínica por email ── -->
    <template v-if="findMode">
      <!-- Logo visível só em mobile (no desktop o painel esquerdo tem) -->
      <div class="flex items-center gap-2.5 mb-8 lg:hidden">
        <div class="brand-icon-sm">
          <UIcon name="i-heroicons-heart" class="w-4 h-4 text-white" />
        </div>
        <span class="text-lg font-bold text-gray-800 tracking-tight">{{ appName }}</span>
      </div>

      <div class="mb-8">
        <h2 class="text-2xl font-bold text-gray-900">Acesse sua clínica</h2>
        <p class="mt-1 text-sm text-gray-500">Digite seu e-mail para encontrar o acesso</p>
      </div>

      <!-- Lista de resultados -->
      <div v-if="findResults.length" class="space-y-3">
        <p class="text-sm text-gray-500 mb-4">Selecione sua clínica:</p>
        <button
          v-for="t in findResults"
          :key="t.slug"
          class="clinic-option-btn"
          @click="goToTenant(t.domain, t.name, (t as any)._gc)"
        >
          <div class="clinic-icon">
            <UIcon name="i-heroicons-building-office-2" class="w-5 h-5 text-indigo-600" />
          </div>
          <div class="text-left">
            <p class="font-semibold text-gray-800">{{ t.name }}</p>
            <p class="text-xs text-gray-500">{{ t.domain }}</p>
          </div>
          <UIcon name="i-heroicons-chevron-right" class="w-4 h-4 text-gray-300 ml-auto" />
        </button>
        <button class="text-sm text-gray-500 hover:text-gray-700 mt-2 flex items-center gap-1" @click="findResults = []">
          <UIcon name="i-heroicons-arrow-left" class="w-4 h-4" /> Voltar
        </button>
      </div>

      <!-- Formulário busca por email -->
      <form v-else @submit.prevent="handleFindClinic" class="space-y-5">
        <UFormGroup label="E-mail cadastrado">
          <UInput
            v-model="findEmail"
            type="email"
            placeholder="seu@email.com"
            icon="i-heroicons-envelope"
            :disabled="findLoading || findSocialLoading"
            autofocus
            size="lg"
          />
        </UFormGroup>

        <UButton type="submit" color="primary" block size="lg" :loading="findLoading" :disabled="!findEmail || findSocialLoading">
          Continuar
        </UButton>

        <GoogleSignInButton :loading="findSocialLoading" @credential="handleFindGoogleCredential" />

        <div class="divider-or">
          <span>ou</span>
        </div>

        <div class="flex flex-col items-center gap-1.5 text-sm text-gray-500">
          <span>
            Clínica nova?
            <UButton :to="onboardingUrl" variant="link" color="primary" size="sm" class="font-semibold">
              Criar conta grátis
            </UButton>
          </span>
          <span>
            É paciente?
            <UButton :to="patientPortalUrl" variant="link" color="gray" size="sm" :external="isExternalPortal">
              Acessar portal
            </UButton>
          </span>
        </div>
      </form>
    </template>

    <!-- ── Modo: subdomínio — login normal ── -->
    <template v-else>
      <!-- Logo visível só em mobile -->
      <div class="flex items-center gap-2.5 mb-8 lg:hidden">
        <div class="brand-icon-sm">
          <UIcon name="i-heroicons-heart" class="w-4 h-4 text-white" />
        </div>
        <span class="text-lg font-bold text-gray-800 tracking-tight">{{ appName }}</span>
      </div>

      <!-- Tenant não encontrado (clínica deletada / URL inválida) -->
      <div v-if="tenantNotFound" class="text-center py-6 space-y-4">
        <UIcon name="i-heroicons-building-office-2" class="w-12 h-12 text-gray-300 mx-auto" />
        <div>
          <p class="text-lg font-semibold text-gray-700">Clínica não encontrada</p>
          <p class="text-sm text-gray-500 mt-1">Este endereço não corresponde a nenhuma clínica ativa. Ela pode ter sido removida ou o link está incorreto.</p>
        </div>
        <UButton :to="rootLoginUrl" color="primary" variant="soft" :external="isExternalPortal">
          Buscar minha clínica
        </UButton>
      </div>

      <template v-else>
      <div class="mb-8">
        <div v-if="tenantLoading" class="flex items-center gap-2 mb-1">
          <UIcon name="i-heroicons-arrow-path" class="w-4 h-4 animate-spin text-indigo-400" />
          <span class="text-sm text-gray-400">Carregando...</span>
        </div>
        <template v-else>
          <!-- Botão voltar (só no native quando veio do fluxo de busca) -->
          <button
            v-if="isNativePlatform && (findResults.length > 0 || findEmail)"
            type="button"
            class="flex items-center gap-1 text-sm text-gray-400 hover:text-gray-600 mb-4 -ml-1"
            @click="findMode = true"
          >
            <UIcon name="i-heroicons-arrow-left" class="w-4 h-4" />
            {{ findResults.length > 0 ? 'Escolher outra clínica' : 'Voltar' }}
          </button>

          <div v-if="clinicName" class="flex items-center gap-2 mb-3">
            <div class="w-8 h-8 rounded-lg bg-indigo-100 flex items-center justify-center">
              <UIcon name="i-heroicons-building-office-2" class="w-4 h-4 text-indigo-600" />
            </div>
            <div>
              <p class="text-xs text-gray-500 leading-none">Acessando</p>
              <p class="text-sm font-semibold text-gray-700 leading-tight">{{ clinicName }}</p>
            </div>
          </div>
          <h2 class="text-2xl font-bold text-gray-900">Bem-vindo de volta</h2>
          <p class="mt-1 text-sm text-gray-500">Entre com suas credenciais para continuar</p>
        </template>
      </div>

      <form @submit.prevent="handleLogin" class="space-y-4" autocomplete="on">
        <UFormGroup label="E-mail">
          <UInput
            v-model="email"
            type="email"
            name="email"
            autocomplete="email"
            placeholder="seu@email.com"
            icon="i-heroicons-envelope"
            :disabled="loading"
            size="lg"
          />
        </UFormGroup>

        <UFormGroup label="Senha">
          <UInput
            v-model="password"
            :type="showPassword ? 'text' : 'password'"
            name="password"
            autocomplete="current-password"
            placeholder="••••••••"
            icon="i-heroicons-lock-closed"
            :disabled="loading"
            size="lg"
          >
            <template #trailing>
              <button type="button" tabindex="-1" class="flex items-center justify-center p-1" @click="showPassword = !showPassword">
                <UIcon
                  :name="showPassword ? 'i-heroicons-eye-slash' : 'i-heroicons-eye'"
                  class="text-gray-400 hover:text-gray-600 w-4 h-4"
                />
              </button>
            </template>
          </UInput>
        </UFormGroup>

        <div class="flex items-center justify-between -mt-1">
          <label v-if="isNativePlatform && biometricAvailable && !biometricRegistered" class="flex items-center gap-1.5 cursor-pointer select-none">
            <input v-model="saveWithBiometric" type="checkbox" class="rounded text-indigo-600" />
            <span class="text-xs text-gray-500">Salvar senha</span>
          </label>
          <span v-else />
          <UButton to="/auth/forgot-password" variant="link" color="primary" size="xs" class="text-gray-400 hover:text-indigo-600">
            Esqueceu a senha?
          </UButton>
        </div>

        <UButton type="submit" color="primary" block size="lg" :loading="loading" :disabled="!email || !password">
          Entrar
        </UButton>

        <!-- Login biométrico -->
        <UButton
          v-if="biometricAvailable && biometricRegistered"
          block
          color="gray"
          variant="soft"
          size="lg"
          @click="handleBiometricLogin"
        >
          <UIcon name="i-heroicons-finger-print" class="w-5 h-5 mr-2" />
          Entrar com biometria
        </UButton>

        <!-- Login social -->
        <GoogleSignInButton :loading="socialLoading" @credential="handleGoogleCredential" />

        <div class="divider-or">
          <span>ou</span>
        </div>

        <div class="flex flex-col items-center gap-2 text-sm text-gray-500">
          <span>
            Clínica nova?
            <UButton to="/auth/onboarding" variant="link" color="primary" size="sm" class="font-semibold">
              Criar conta grátis
            </UButton>
          </span>
          <span>
            É paciente?
            <UButton :to="patientPortalUrl" variant="link" color="gray" size="sm" :external="isExternalPortal">
              Acessar portal
            </UButton>
          </span>
          <span v-if="!findMode && !isNativePlatform">
            Outra clínica?
            <UButton :to="rootLoginUrl" variant="link" color="gray" size="sm" :external="isExternalPortal">
              Trocar de clínica
            </UButton>
          </span>
        </div>
      </form>
      </template>
    </template>

    <!-- Modal: ativar biometria após login -->
    <UModal v-model="showBiometricModal">
      <UCard>
        <template #header>
          <div class="text-center">
            <UIcon name="i-heroicons-finger-print" class="w-10 h-10 text-primary-500 mx-auto mb-2" />
            <h3 class="text-lg font-semibold">Ativar biometria?</h3>
          </div>
        </template>
        <p class="text-sm text-gray-500 text-center">
          Deseja ativar login por impressão digital nos próximos acessos?
        </p>
        <template #footer>
          <div class="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <UButton variant="ghost" color="gray" @click="dismissBiometricModal">Agora não</UButton>
            <UButton color="primary" @click="confirmBiometricRegister">Sim, ativar</UButton>
          </div>
        </template>
      </UCard>
    </UModal>

  </div>
</template>

<script setup lang="ts">
import { Capacitor } from '@capacitor/core';
definePageMeta({
  layout: "auth-login",
  middleware: "guest",
});

const VALID_HOME_PAGES = ['/app/dashboard', '/app/agenda', '/app/checklist', '/app/pacientes', '/app/prontuario', '/app/chat', '/app/financeiro', '/app/relatorios']
function safeHomePage(page?: string | null): string {
  return (page && VALID_HOME_PAGES.includes(page)) ? page : '/app/dashboard'
}

const router  = useRouter();
const route   = useRoute();
const toast   = useToast();
const auth    = useAuthStore();
const config  = useRuntimeConfig();
const { public: { appName } } = config;
const apiBase = config.public.apiBase as string;

const isNativePlatform = Capacitor.isNativePlatform();
const { defaultBase, activeBase, setBase, resetBase } = useApiBase();
// No native, cada visita à login page reseta o base para o endpoint público de descoberta.
// Isso garante que stale state do activeBase (clínica anterior) não interfira.
if (isNativePlatform && import.meta.client) resetBase();

// ── Modo "encontrar clínica" (domínio principal) ──────────────────────────────
// No native sempre começa em findMode: email → descobrir clínica → login
const findMode      = ref(false);
const onboardingUrl = ref('/auth/onboarding');
const findEmail     = ref("");
const findLoading   = ref(false);

interface TenantResult { slug: string; name: string; domain: string }
const findResults = ref<TenantResult[]>([]);
const findSocialLoading = ref(false);

const clinicName    = ref("");
const tenantLoading = ref(false);
const tenantNotFound = ref(false);

// ── Biometria ─────────────────────────────────────────────────────────────────
const biometricAvailable    = ref(false);
const biometricRegistered   = ref(false);
const showBiometricModal    = ref(false);
const postLoginDestination  = ref('/app/dashboard');
let biometricAuth: ReturnType<typeof useBiometricAuth> | null = null;

async function checkBiometric() {
  try {
    biometricAuth = useBiometricAuth();
    biometricAvailable.value = await biometricAuth.checkAvailability();
    if (biometricAvailable.value) {
      biometricRegistered.value = await biometricAuth.isRegistered();
    }
  } catch {
    biometricAvailable.value = false;
  }
}

async function handleBiometricLogin() {
  if (!biometricAuth) return;
  try {
    const { refreshToken } = await biometricAuth.authenticate();
    const api = useApi();
    const { data } = await api.post('/auth/refresh', { refresh: refreshToken });
    const payload = data.data ?? data;
    auth.accessToken = payload.access;
    auth.refreshToken = payload.refresh;
    if (payload.user) auth.user = payload.user;
    router.push(safeHomePage(auth.user?.home_page));
  } catch (err: any) {
    if (err?.message !== 'Plugin not installed') {
      toast.add({ title: 'Falha na autenticação', description: 'Tente fazer login com senha.', color: 'red' });
    }
  }
}

const rootLoginUrl = computed(() => {
  const bd = config.public.baseDomain as string
  return bd === 'localhost' ? '/auth/login' : `https://${bd}/auth/login`
})

const patientPortalUrl = computed(() => {
  const bd = config.public.baseDomain as string
  if (bd === 'localhost' || isNativePlatform) return '/auth/portal-paciente'
  return `https://pacientes.${bd}`
})
const isExternalPortal = computed(() => !isNativePlatform && config.public.baseDomain !== 'localhost')

const isPatientPortalHost = ref(false)

onMounted(async () => {
  const bd    = config.public.baseDomain as string;
  const host  = window.location.hostname;

  // No native (Capacitor): sempre inicia em findMode para descobrir a clínica pelo email
  if (isNativePlatform) {
    findMode.value = true;
    checkBiometric();
    return;
  }

  const isMain = host === bd || host === `www.${bd}`;

  // Subdomínio pacientes.* → redirecionar para a página dedicada
  if (host === `pacientes.${bd}` || (bd === 'localhost' && host === 'pacientes.localhost')) {
    isPatientPortalHost.value = true
    await navigateTo('/auth/portal-paciente')
    return
  }

  findMode.value = isMain;
  if (!isMain && bd !== 'localhost') {
    onboardingUrl.value = `https://${bd}/auth/onboarding`;
  }

  checkBiometric();

  const queryClinic = route.query.clinic as string;
  if (queryClinic) clinicName.value = queryClinic;

  const queryEmail = route.query.email as string;
  if (queryEmail) email.value = queryEmail;

  // Vindo do domínio raiz via Google — autentica automaticamente
  const gc = route.query.gc as string | undefined;
  if (gc && !isMain) {
    nextTick(() => handleGoogleCredential(gc));
  }

  if (!isMain) {
    tenantLoading.value = true;
    try {
      const info = (await $fetch(`${apiBase}/auth/tenant-info/`)) as { tenant?: any; clinic?: { name: string } };
      if (info?.clinic?.name) {
        clinicName.value = info.clinic.name;
      } else if (!info?.tenant && !info?.clinic) {
        // Subdomínio sem tenant válido — clínica foi removida ou URL inválida
        tenantNotFound.value = true;
      }
    } catch {
      // sem tenant-info — segue
    } finally {
      tenantLoading.value = false;
    }
  }
});

async function handleFindClinic() {
  findLoading.value = true;
  let tenants: TenantResult[] = [];
  try {
    // find-tenant/ sempre no endpoint público de descoberta, não no da clínica
    const discoveryBase = isNativePlatform ? defaultBase : apiBase;
    const data = (await $fetch(`${discoveryBase}/find-tenant/`, {
      method: "POST",
      body: { email: findEmail.value },
    })) as { tenants: TenantResult[] };
    tenants = data.tenants ?? [];
  } catch {
    toast.add({ title: "Erro", description: "Falha ao buscar clínica. Verifique sua conexão.", color: "red" });
    findLoading.value = false;
    return;
  }
  findLoading.value = false;

  if (tenants.length === 1) {
    goToTenant(tenants[0].domain, tenants[0].name);
  } else if (tenants.length > 1) {
    findResults.value = tenants;
  } else {
    toast.add({ title: "Não encontrado", description: "Nenhuma clínica encontrada para este e-mail.", color: "red" });
  }
}

function goToTenant(domain: string, name?: string, gc?: string) {
  if (isNativePlatform) {
    // No native: troca o base URL da API para o domínio da clínica selecionada
    // e transiciona para o formulário de login sem sair do app.
    setBase(`https://${domain}/api/v1`);
    clinicName.value = name || '';
    email.value = findEmail.value;
    findMode.value = false;
    if (gc) nextTick(() => handleGoogleCredential(gc));
    return;
  }
  const params = new URLSearchParams({ email: findEmail.value });
  if (name) params.set("clinic", name);
  if (gc) params.set("gc", gc);
  window.location.href = `https://${domain}/auth/login?${params}`;
}

async function handleFindGoogleCredential(credential: string) {
  findSocialLoading.value = true;
  try {
    // Decodifica o payload do JWT Google (não precisa verificar assinatura — só queremos o email para a busca)
    const payload = JSON.parse(atob(credential.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
    const googleEmail = payload.email as string;
    if (!googleEmail) throw new Error("sem email");

    const discoveryBase = isNativePlatform ? defaultBase : apiBase;
    const data = (await $fetch(`${discoveryBase}/find-tenant/`, {
      method: "POST",
      body: { email: googleEmail },
    })) as { tenants: TenantResult[] };
    const tenants = data.tenants ?? [];
    if (tenants.length === 1) {
      goToTenant(tenants[0].domain, tenants[0].name, credential);
    } else if (tenants.length > 1) {
      findEmail.value = googleEmail;
      findResults.value = tenants.map(t => ({ ...t, _gc: credential })) as any;
    } else {
      toast.add({ title: "Não encontrado", description: "Nenhuma clínica encontrada para este e-mail do Google.", color: "red" });
    }
  } catch {
    toast.add({ title: "Erro", description: "Não foi possível autenticar com Google.", color: "red" });
  } finally {
    findSocialLoading.value = false;
  }
}

// ── Login normal (subdomínio) ─────────────────────────────────────────────────
const email        = ref("");
const password         = ref("");
const showPassword     = ref(false);
const saveWithBiometric = ref(false);
const loading      = ref(false);

/** Retorna true se o modal foi exibido (navegar depois). false = navegar agora. */
async function offerBiometricRegister(destination: string): Promise<boolean> {
  if (!isNativePlatform) return false;
  if (!biometricAuth) {
    biometricAuth = useBiometricAuth();
    biometricAvailable.value = await biometricAuth.checkAvailability();
  }
  if (!biometricAvailable.value) return false;
  biometricRegistered.value = await biometricAuth.isRegistered();
  if (biometricRegistered.value) return false;
  postLoginDestination.value = destination;
  showBiometricModal.value = true;
  return true;
}

async function confirmBiometricRegister() {
  try {
    await biometricAuth!.register(auth.user!.email, auth.refreshToken!);
    biometricRegistered.value = true;
    toast.add({ title: 'Biometria ativada!', color: 'green' });
  } catch {
    toast.add({ title: 'Falha ao ativar biometria', color: 'red' });
  }
  showBiometricModal.value = false;
  await router.push(postLoginDestination.value);
}

async function dismissBiometricModal() {
  showBiometricModal.value = false;
  await router.push(postLoginDestination.value);
}

async function handleLogin() {
  loading.value = true;
  try {
    const result = await auth.login(email.value, password.value);
    if (result.requires_2fa) {
      return router.push({ path: "/auth/verify-2fa", query: { token: result.temp_token, email: result.email } });
    }
    if (result.requires_clinic_selection) {
      if (isNativePlatform) {
        // No native: tenta auto-selecionar a clínica já escolhida no findMode
        // activeBase já foi setado para https://{domain}/api/v1 via goToTenant()
        // activeBase = https://{slug}.saude.aevix.dev/api/v1 → extrai o slug
        const currentSlug = activeBase.value.replace('https://', '').split('.')[0]
        const match = auth.pendingClinics.find(c => c.slug === currentSlug)
        if (match) {
          await auth.selectClinic(match.schema)
          const dest = safeHomePage(auth.user?.home_page)
          if (saveWithBiometric.value && biometricAvailable.value) {
            try {
              if (!biometricAuth) biometricAuth = useBiometricAuth()
              await biometricAuth.register(auth.user!.email, auth.refreshToken!)
              biometricRegistered.value = true
              toast.add({ title: 'Senha salva com biometria!', color: 'green' })
            } catch {}
          }
          return router.push(dest)
        }
        return router.push("/auth/selecionar-clinica");
      }
      // Na web: se já estamos no subdomínio da clínica, seleciona automaticamente
      const bd = config.public.baseDomain as string;
      const host = window.location.hostname;
      const isSubdomain = bd && bd !== 'localhost' && host !== bd && host !== `www.${bd}`;
      if (isSubdomain) {
        const currentSlug = host.replace(`.${bd}`, '');
        const match = auth.pendingClinics.find(c => c.slug === currentSlug);
        if (match) {
          await auth.selectClinic(match.schema);
          return router.push(safeHomePage(auth.user?.home_page));
        }
      }
      return router.push("/auth/selecionar-clinica");
    }
    if (clinicName.value && !auth.clinicName) auth.setClinicName(clinicName.value);
    const dest = safeHomePage(auth.user?.home_page);
    if (saveWithBiometric.value && isNativePlatform && biometricAvailable.value) {
      // Salvar senha com biometria sem mostrar modal
      try {
        if (!biometricAuth) biometricAuth = useBiometricAuth();
        await biometricAuth.register(auth.user!.email, auth.refreshToken!);
        biometricRegistered.value = true;
        toast.add({ title: 'Senha salva com biometria!', color: 'green' });
      } catch {}
      await router.push(dest);
    } else {
      const showed = await offerBiometricRegister(dest);
      if (!showed) await router.push(dest);
    }
  } catch (err: any) {
    toast.add({
      title: "Falha no login",
      description: extractApiError(err, "E-mail ou senha inválidos."),
      color: "red",
    });
  } finally {
    loading.value = false;
  }
}

// ── Social auth (Google) ────────────────────────────────────────────────────
const socialLoading = ref(false);

async function handleGoogleCredential(credential: string) {
  socialLoading.value = true;
  try {
    const data = await $fetch(`${activeBase.value}/auth/social/google`, {
      method: "POST",
      body: { credential },
    }) as any;
    await handleAuthResponse(data.data);
  } catch (err: any) {
    toast.add({
      title: "Falha no login com Google",
      description: extractApiError(err, "Não foi possível autenticar. Tente novamente."),
      color: "red",
    });
  } finally {
    socialLoading.value = false;
  }
}

async function handleAuthResponse(data: any) {
  if (data.requires_2fa_setup) {
    // 2FA obrigatório mas não configurado → entra no app e redireciona para setup
    auth.setTokens(data.tokens);
    auth.user = data.user;
    return router.push("/app/configuracoes/perfil?setup2fa=1");
  }
  if (data.requires_2fa) {
    auth.setPendingClinicSelection([], data.temp_token);
    return router.push("/auth/verify-2fa");
  }
  if (data.requires_clinic_selection) {
    auth.setPendingClinicSelection(data.clinics, data.temp_token);
    return router.push("/auth/selecionar-clinica");
  }
  auth.setTokens(data.tokens);
  auth.user = data.user;
  if (clinicName.value && !auth.clinicName) auth.setClinicName(clinicName.value);
  await router.push(safeHomePage(data.user?.home_page));
}
</script>

<style scoped>
.brand-icon-sm {
  width: 1.875rem;
  height: 1.875rem;
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
  border-radius: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.clinic-option-btn {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.875rem;
  padding: 0.875rem 1rem;
  border: 1.5px solid #e5e7eb;
  border-radius: 0.75rem;
  background: #fff;
  cursor: pointer;
  transition: border-color 0.15s, box-shadow 0.15s, background 0.15s;
  text-align: left;
}
.clinic-option-btn:hover {
  border-color: #6366f1;
  background: #fafafa;
  box-shadow: 0 2px 8px rgba(99,102,241,0.1);
}

.clinic-icon {
  width: 2.5rem;
  height: 2.5rem;
  background: #eef2ff;
  border-radius: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.divider-or {
  position: relative;
  text-align: center;
  margin: 0.25rem 0;
}
.divider-or::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  height: 1px;
  background: #e5e7eb;
}
.divider-or span {
  position: relative;
  background: #fff;
  padding: 0 0.75rem;
  font-size: 0.75rem;
  color: #9ca3af;
}
</style>
